import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { X, Plus, Trash2, Check, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function ProposalEditor({ proposal, onSave, onCancel }) {
    const { API } = useContext(AuthContext);
    const [description, setDescription] = useState(proposal.description || '');
    const [date, setDate] = useState(proposal.date || new Date().toISOString().split('T')[0]);
    const [entries, setEntries] = useState(proposal.entries || []);
    const [accounts, setAccounts] = useState([]);

    // Calculate totals
    const totalDebit = entries.reduce((sum, e) => sum + (parseFloat(e.debit) || 0), 0);
    const totalCredit = entries.reduce((sum, e) => sum + (parseFloat(e.credit) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 1.0;

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const response = await axios.get(`${API}/chart-of-accounts`);
                setAccounts(response.data);
            } catch (error) {
                toast.error('Error al cargar plan de cuentas');
            }
        };
        loadAccounts();

        // Normalize entries on load
        if (proposal.entries) {
            setEntries(proposal.entries.map(e => ({
                ...e,
                debit: parseFloat(e.debit) || 0,
                credit: parseFloat(e.credit) || 0
            })));
        }
    }, [proposal, API]);

    const updateEntry = (index, field, value) => {
        const newEntries = [...entries];
        newEntries[index] = { ...newEntries[index], [field]: value };

        // If updating account code, find name
        if (field === 'account_code') {
            const account = accounts.find(a => a.code === value);
            if (account) {
                newEntries[index].account_name = account.name;
            }
        }

        setEntries(newEntries);
    };

    const removeEntry = (index) => {
        setEntries(entries.filter((_, i) => i !== index));
    };

    const addEntry = () => {
        setEntries([...entries, { account_code: '', account_name: '', debit: 0, credit: 0 }]);
    };

    const handleSave = async () => {
        if (!description) {
            toast.error('Falta la descripción');
            return;
        }
        if (!isBalanced) {
            toast.error('El asiento no cuadra');
            return;
        }
        if (entries.length < 2) {
            toast.error('El asiento debe tener al menos 2 líneas');
            return;
        }

        const transactionData = {
            date,
            description,
            entries: entries.map(e => ({
                account_code: e.account_code,
                account_name: e.account_name,
                debit: parseFloat(e.debit) || 0,
                credit: parseFloat(e.credit) || 0
            })),
            source: 'ai_assisted'
        };

        onSave(transactionData);
    };

    return (
        <Card className="w-full border-2 border-primary/20 shadow-xl">
            <CardHeader className="bg-muted/30">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-xl flex items-center gap-2">
                        ✨ Propuesta de Asiento Contable
                        {proposal.confidence && (
                            <span className={`text-xs px-2 py-1 rounded-full ${proposal.confidence > 0.8 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                Confianza IA: {Math.round(proposal.confidence * 100)}%
                            </span>
                        )}
                    </CardTitle>
                    <Button variant="ghost" size="icon" onClick={onCancel}>
                        <X className="h-5 w-5" />
                    </Button>
                </div>

                {proposal.analysis_notes && (
                    <div className="text-sm text-muted-foreground bg-blue-50 text-blue-800 p-2 rounded mt-2">
                        💡 Nota: {proposal.analysis_notes}
                    </div>
                )}
            </CardHeader>

            <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                        <label className="text-sm font-medium mb-1 block">Descripción</label>
                        <Input
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Ej: Pago factura combustible"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-medium mb-1 block">Fecha</label>
                        <Input
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </div>
                </div>

                <div className="border rounded-md overflow-hidden">
                    <Table>
                        <TableHeader className="bg-muted/50">
                            <TableRow>
                                <TableHead className="w-[40%]">Cuenta</TableHead>
                                <TableHead className="w-[100px] text-right">Debe</TableHead>
                                <TableHead className="w-[100px] text-right">Haber</TableHead>
                                <TableHead className="w-[50px]"></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entries.map((entry, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <Select
                                            value={entry.account_code || ''}
                                            onValueChange={(val) => updateEntry(index, 'account_code', val)}
                                        >
                                            <SelectTrigger className="h-8">
                                                <SelectValue placeholder="Seleccionar cuenta" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {accounts.map(acc => (
                                                    <SelectItem key={acc.code} value={acc.code}>
                                                        {acc.code} - {acc.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            type="number"
                                            className="h-8 text-right"
                                            value={entry.debit}
                                            onChange={(e) => updateEntry(index, 'debit', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Input
                                            type="number"
                                            className="h-8 text-right"
                                            value={entry.credit}
                                            onChange={(e) => updateEntry(index, 'credit', e.target.value)}
                                            onFocus={(e) => e.target.select()}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => removeEntry(index)}>
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Button variant="ghost" size="sm" className="w-full border-dashed border" onClick={addEntry}>
                                        <Plus className="h-4 w-4 mr-2" /> Agregar línea
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </div>

                <div className="flex justify-between items-center bg-muted/20 p-4 rounded-lg">
                    <div className="text-sm font-medium">Totales</div>
                    <div className="flex gap-8">
                        <div className={`text-lg font-bold ${!isBalanced ? 'text-red-500' : 'text-green-600'}`}>
                            Debe: ${totalDebit.toLocaleString('es-CL')}
                        </div>
                        <div className={`text-lg font-bold ${!isBalanced ? 'text-red-500' : 'text-green-600'}`}>
                            Haber: ${totalCredit.toLocaleString('es-CL')}
                        </div>
                    </div>
                </div>

                {!isBalanced && (
                    <div className="flex items-center gap-2 text-red-500 text-sm animate-pulse">
                        <AlertTriangle className="h-4 w-4" />
                        La transacción no cuadra. Diferencia: ${(totalDebit - totalCredit).toLocaleString('es-CL')}
                    </div>
                )}
            </CardContent>

            <CardFooter className="flex justify-end gap-2 bg-muted/50 p-4">
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button onClick={handleSave} disabled={!isBalanced || entries.length === 0} className="bg-green-600 hover:bg-green-700">
                    <Check className="mr-2 h-4 w-4" /> Guardar Pendiente de Cruce
                </Button>
            </CardFooter>
        </Card>
    );
}
